import { makeVar, ReactiveVar } from '@apollo/client'

import { SERVER_MESSAGE_DEFAULT } from 'shared/consts'
import {
  FEED_FILTER_INIT_VALUES,
  FEED_FILTER_STORAGE_NAME,
  SUCCESS_STORIES_FILTER_INIT_VALUES,
  SUCCESS_STORIES_FILTER_STORAGE_NAME
} from 'shared/consts/filter'
import {
  ChartFreshness,
  IArtist,
  IFactSource,
  IFollowedTag,
  INotification,
  IProduct,
  IProductMatch,
  IRole,
  ISpace,
  IUser
} from 'shared/interfaces'
import { TAlert, TSearch, TSelectOption } from 'shared/types'
import { getFilter, getFiltersFromLocalStorage } from 'shared/utils/filters'
import { GetCampaignViewsResponse, Tag } from 'shared/__gql-types'

export const CommonStore = {
  activeProject: {
    read: () => activeProjectVar()
  },
  activeArtist: {
    read: () => activeArtistVar()
  },
  activeStep: {
    read: () => activeStepVar()
  },
  countryCode: {
    read: () => countryCodeVar()
  },
  labelCode: {
    read: () => labelCodeVar()
  },
  currentUser: {
    read: () => currentUserVar()
  },
  currentUserLoadedVar: {
    read: () => currentUserLoadedVar()
  },
  factSources: {
    read: () => factSourcesVar()
  },
  feedFilters: {
    read: () => feedFiltersVar()
  },
  successStoriesFilter: {
    read: () => successStoriesFilterVar()
  },
  gpsUnits: {
    read: () => gpsUnitsVar()
  },
  isFirstLogin: {
    read: () => isFirstLoginVar()
  },
  isLoading: {
    read: () => isLoadingVar()
  },
  labelCodeId: {
    read: () => labelCodeIdVar()
  },
  spaceId: {
    read: () => spaceIdVar()
  },
  globalProjectId: {
    read: () => globalProjectIdVar()
  },
  spacesList: {
    read: () => spacesListVar()
  },
  isImageError: {
    read: () => isImageErrorVar()
  },
  isEmbededError: {
    read: () => isImageErrorVar()
  },
  isServerErrorVar: {
    read: () => isServerErrorVar()
  },
  labelTagsError: {
    read: () => labelTagsErrorVar()
  },
  mostViewedProjects: {
    read: () => mostViewedProjectsVar()
  },
  notificationsTags: {
    read: () => notificationsTagsVar()
  },
  newNotification: {
    read: () => newNotificationVar()
  },
  matchedProduct: {
    read: () => matchedProductVar()
  },
  redirectAlert: {
    read: () => redirectAlertVar()
  },
  serverErrorMessage: {
    read: () => serverErrorMessageVar()
  },
  searchName: {
    read: () => searchNameVar()
  },
  searchResult: {
    read: () => searchResultVar()
  },
  storyGuide: {
    read: () => storyGuideVar()
  },
  token: {
    read: () => tokenVar()
  },
  userFollows: {
    read: () => userFollowsVar()
  },
  userRoles: {
    read: () => userRolesVar()
  },
  userSearchName: {
    read: () => userSearchNameVar()
  },
  projectFeedTotal: {
    read: () => projectFeedTotalVar()
  },
  globalProjectsTotal: {
    read: () => globalProjectsTotalVar()
  },
  globalProjectFeedTotal: {
    read: () => globalProjectFeedTotalVar()
  },
  projectProductsTotal: {
    read: () => projectProductsTotalVar()
  },
  projectTourTotal: {
    read: () => projectTourTotalVar()
  },
  owner: {
    read: () => ownerVar()
  },
  toggleMenu: {
    read: () => toggleMenuVar()
  },
  followedTags: {
    read: () => followedTagsVar()
  },
  followedArtists: {
    read: () => followedArtistsVar()
  },
  projectsForFollow: {
    read: () => projectsForFollowVar()
  },
  shouldResetAllFilters: {
    read: () => shouldResetAllFiltersVar()
  },
  isZoneChanged: {
    read: () => isZoneChangedVar()
  },
  showGlobalizedFeedback: {
    read: () => showGlobalizedFeedbackVar()
  },
  showGlobalizedFeedbackForm: {
    read: () => showGlobalizedFeedbackFormVar()
  },
  artistProducts: {
    read: () => artistProductsVar()
  },
  artist: {
    read: () => artistVar()
  },
  notificationMessage: {
    read: () => notificationMessageVar()
  },
  feedTotal: {
    read: () => feedTotalVar()
  },
  showMyFilterForZone: {
    read: () => showMyFilterForZoneVar()
  },
  showArchivedDialog: {
    read: () => showArchivedDialogVar()
  },
  showArchivedGrowl: {
    read: () => showArchivedGrowlVar()
  },
  globalizeFeedFilter: {
    read: () => globalizeFeedFilterVar()
  },
  successStoriesTotal: {
    read: () => successStoriesTotalVar()
  }
}

export const activeStepVar: ReactiveVar<number> = makeVar<number>(0)
export const projectFeedTotalVar: ReactiveVar<number> = makeVar<number>(0)
export const feedTotalVar: ReactiveVar<number> = makeVar<number>(0)
export const globalProjectsTotalVar: ReactiveVar<number> = makeVar<number>(0)
export const globalProjectFeedTotalVar: ReactiveVar<number> = makeVar<number>(0)
export const projectProductsTotalVar: ReactiveVar<number> = makeVar<number>(0)
export const projectTourTotalVar: ReactiveVar<number> = makeVar<number>(0)
export const activeProjectVar: ReactiveVar<any> = makeVar<any>({})
export const activeArtistVar: ReactiveVar<any> = makeVar<any>({})
export const countryCodeVar: ReactiveVar<string> = makeVar<string>('')
/**
 * @deprecated
 * Remove this property, it's can be calculated
 */
export const labelCodeVar: ReactiveVar<string> = makeVar<string>('')
export const currentUserVar: ReactiveVar<IUser> = makeVar<any>({})
export const currentUserLoadedVar: ReactiveVar<boolean> = makeVar<boolean>(false)
export const factSourcesVar: ReactiveVar<IFactSource[]> = makeVar<IFactSource[]>([])
export const feedFiltersVar: ReactiveVar<any> = makeVar<any>(
  getFilter(FEED_FILTER_STORAGE_NAME, FEED_FILTER_INIT_VALUES)
)
export const successStoriesFilterVar: ReactiveVar<any> = makeVar<any>(
  getFiltersFromLocalStorage(SUCCESS_STORIES_FILTER_STORAGE_NAME, SUCCESS_STORIES_FILTER_INIT_VALUES)
)
export const gpsUnitsVar: ReactiveVar<TSelectOption[]> = makeVar<TSelectOption[]>([])
export const isEmbededErrorVar: ReactiveVar<boolean> = makeVar<boolean>(false)
export const isFirstLoginVar: ReactiveVar<boolean> = makeVar<boolean>(false)
export const isImageErrorVar: ReactiveVar<boolean> = makeVar<boolean>(false)
export const isLoadingVar: ReactiveVar<boolean> = makeVar<boolean>(false)
export const isServerErrorVar: ReactiveVar<boolean> = makeVar<boolean>(false)
export const isShownAddNewFeedVar: ReactiveVar<boolean> = makeVar<boolean>(true)
export const labelCodeIdVar: ReactiveVar<string> = makeVar<string>('')
export const spaceIdVar: ReactiveVar<string> = makeVar<string>('')
export const globalProjectIdVar: ReactiveVar<string> = makeVar<string>('')

/**
 * @deprecated
 * Remove this property, get title from dictionary
 */
export const labelTitleVar: ReactiveVar<string> = makeVar<string>('')
/**
 * @deprecated
 * Remove this property, use hook
 */
export const spacesListVar: ReactiveVar<ISpace[]> = makeVar<ISpace[]>([])
export const labelTagsErrorVar: ReactiveVar<boolean> = makeVar<boolean>(false)
export const mostViewedProjectsVar: ReactiveVar<GetCampaignViewsResponse[]> = makeVar<GetCampaignViewsResponse[]>([])
export const notificationsTagsVar: ReactiveVar<INotification[]> = makeVar<INotification[]>([])
export const redirectAlertVar: ReactiveVar<boolean> = makeVar<boolean>(false)
export const serverErrorMessageVar: ReactiveVar<string> = makeVar<string>(SERVER_MESSAGE_DEFAULT)
export const searchNameVar: ReactiveVar<string> = makeVar('')
export const searchResultVar: ReactiveVar<any> = makeVar({})
export const storyGuideVar: ReactiveVar<string> = makeVar<string>('')
export const tokenVar: ReactiveVar<any> = makeVar<any>('')
export const userFollowsVar: ReactiveVar<any> = makeVar<any>(undefined)
export const userRolesVar: ReactiveVar<IRole[]> = makeVar<IRole[]>([])
export const userSearchNameVar: ReactiveVar<string> = makeVar<string>('')
export const ownerVar: ReactiveVar<IUser> = makeVar<any>({})
export const toggleMenuVar: ReactiveVar<boolean> = makeVar<boolean>(false)
export const newNotificationVar: ReactiveVar<any> = makeVar<any>({})
export const matchedProductVar: ReactiveVar<IProductMatch | null> = makeVar<IProductMatch | null>(null)
export const followedTagsVar: ReactiveVar<IFollowedTag[]> = makeVar<IFollowedTag[]>([])
export const followedArtistsVar: ReactiveVar<IArtist[]> = makeVar<IArtist[]>([])
export const projectsForFollowVar: ReactiveVar<TSearch> = makeVar<TSearch>({})
export const shouldResetAllFiltersVar: ReactiveVar<boolean> = makeVar<boolean>(false)
export const isZoneChangedVar: ReactiveVar<boolean> = makeVar<boolean>(false)
export const showGlobalizedFeedbackVar: ReactiveVar<boolean> = makeVar<boolean>(false)
export const showGlobalizedFeedbackFormVar: ReactiveVar<boolean> = makeVar<boolean>(false)
export const artistProductsVar: ReactiveVar<IProduct[]> = makeVar<IProduct[]>([])
export const artistVar: ReactiveVar<IArtist | null> = makeVar<IArtist | null>(null)
export const notificationMessageVar: ReactiveVar<TAlert | null> = makeVar<TAlert | null>(null)
export const chartFreshnessVar: ReactiveVar<ChartFreshness[]> = makeVar<ChartFreshness[]>([])
export const showMyFilterForZoneVar: ReactiveVar<boolean> = makeVar<boolean>(false)
export const showArchivedDialogVar: ReactiveVar<boolean> = makeVar<boolean>(false)
export const showArchivedGrowlVar: ReactiveVar<boolean> = makeVar<boolean>(false)
export const globalizeFeedFilterVar: ReactiveVar<Tag[]> = makeVar<Tag[]>([])
export const successStoriesTotalVar: ReactiveVar<number> = makeVar<number>(0)
